/**
 * Profile Completion Screen
 *
 * Final step in login flow for Basic users missing required profile information.
 * Collects email/phone and legal agreements (ToS, Privacy Policy, Communication Preferences).
 * Self-contained component that fetches user data and handles profile update.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { InputPhone, useToastr } from '@farmshare/ui-components';
import { Form, Formik } from 'formik';
import {
  useUpdateProfileMutation,
  useUserTenantVendorQuery,
} from '../../../lib/graphql';

export interface CompleteProfileFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  acceptTos: boolean;
  acceptPrivacyPolicy: boolean;
  canReceiveEmailUpdates: boolean;
  canReceiveSms: boolean;
}

interface CompleteProfileScreenProps {
  onComplete: () => void;
}

export function CompleteProfileScreen({
  onComplete,
}: CompleteProfileScreenProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { push } = useToastr();

  // Fetch current user data
  const { data, loading } = useUserTenantVendorQuery();
  const [updateProfile] = useUpdateProfileMutation();

  const user = data?.userTenantVendor;

  const defaultValues: CompleteProfileFormValues = {
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    acceptTos: false,
    acceptPrivacyPolicy: false,
    canReceiveEmailUpdates: true,
    canReceiveSms: true,
  };

  const handleSubmit = async (values: CompleteProfileFormValues) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await updateProfile({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName || undefined,
          email: values.email || undefined,
          phone: values.phone || undefined,
          company: values.company || undefined,
          acceptTos: values.acceptTos,
          acceptPrivacyPolicy: values.acceptPrivacyPolicy,
          canReceiveEmailUpdates: values.canReceiveEmailUpdates,
          canReceiveSms: values.canReceiveSms,
        },
      });

      push({
        title: 'Your profile is set up',
        body: "Thanks — you're all set.",
        bg: 'success',
      });

      onComplete();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to update profile';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Complete Your Profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please complete your profile to continue
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Formik
        initialValues={defaultValues}
        validate={(values) => {
          const errors: Record<string, string> = {};

          if (!values.firstName?.trim()) {
            errors.firstName = 'First name is required';
          }

          if (!values.email?.trim() && !values.phone?.trim()) {
            errors.email = 'Email or phone number is required';
            errors.phone = 'Email or phone number is required';
          }

          if (
            values.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)
          ) {
            errors.email = 'Invalid email address';
          }

          if (!values.acceptTos) {
            errors.acceptTos =
              'You must accept the Terms of Service & Privacy Policy';
          }

          return errors;
        }}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            <Stack spacing={1}>
              {/* First Name */}
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
                required
              />

              {/* Last Name */}
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
              />

              {/* Email */}
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && Boolean(errors.email)}
                helperText={
                  touched.email && errors.email
                    ? errors.email
                    : 'Email or phone number required'
                }
              />

              {/* Phone */}
              <Box>
                <InputPhone label="Phone" nameOverride="phone" />
                {!touched.phone && (
                  <Typography variant="caption" color="text.secondary">
                    Email or phone number required
                  </Typography>
                )}
                {touched.phone && errors.phone && (
                  <Typography variant="caption" color="error">
                    {errors.phone}
                  </Typography>
                )}
              </Box>

              {/* Company */}
              <TextField
                fullWidth
                label="Company (Optional)"
                name="company"
                value={values.company}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              {/* Legal Agreements */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="acceptTos"
                      checked={values.acceptTos}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I accept the{' '}
                      <Link
                        href="https://farmshare.co/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </Link>{' '}
                      &{' '}
                      <Link
                        href="https://farmshare.co/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </Link>
                    </Typography>
                  }
                />
                {touched.acceptTos && errors.acceptTos && (
                  <Typography color="error" variant="caption" display="block">
                    {errors.acceptTos}
                  </Typography>
                )}
              </Box>

              {/* Communication Preferences */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="canReceiveEmailUpdates"
                      checked={values.canReceiveEmailUpdates}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to receive job updates via email, or SMS if I
                      provide a phone number
                    </Typography>
                  }
                />
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                sx={{ mt: 3, position: 'sticky', bottom: 0, zIndex: 1 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Complete Profile'
                )}
              </Button>
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
